import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { DataGrid } from '@/components/data-grid/core/DataGrid';
import { KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import {
  useGetYachtsQuery,
  useDeleteYachtMutation,
  useGetClubByIdQuery,
  yachtManagementApi,
} from '@/services/YatchManagementService';
import { setYachtsResponse } from '@/store/yachtSlice';
import { IClubData } from '@/types/club-management.types';
import { IYachtData } from '@/types/yacht-management.types';
import { ConfirmationDialog } from '@/components/dialogBox/ConfirmationDialog';
import { useState, useEffect, useMemo } from 'react';
import { PaginationState } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';
import { setClubCapabilities } from '@/store/clubCapabilitiesSlice';

interface Harbor {
  name: string;
  location: string;
}

interface DataGridRowProps {
  row: {
    original: IYachtData & {
      harbors?: Harbor[];
      yachtClubAdmin?: {
        id: string;
        name: string;
        email: string;
        profile_picture: string | null;
      };
    };
  };
}

export const YatchListing = ({
  errors,
}: {
  errors?: {
    fetch?: string;
  };
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [deleteYacht] = useDeleteYachtMutation();
  const { resetSearch } = usePagination();
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });
  const [modalState] = useState<{
    isOpen: boolean;
    mode: 'view' | 'edit' | 'create';
    yachtId?: string;
  }>({ isOpen: false, mode: 'view' });
  const [deleteState, setDeleteState] = useState<{
    isOpen: boolean;
    yachtId: string | null;
  }>({ isOpen: false, yachtId: null });
  const [, setSelectedClub] = useState<IClubData | null>(null);
  const { hasPermission } = usePermissions();

  const {
    data: yachtsResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetYachtsQuery(
    {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedSearchTerm.trim() || undefined,
    },
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    }
  );

  const { data: clubData } = useGetClubByIdQuery(modalState.yachtId || '', {
    skip: !modalState.yachtId || !modalState.isOpen,
  });

  useEffect(() => {
    if (clubData) {
      setSelectedClub(clubData);
    }
  }, [clubData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    refetch();
  }, [debouncedSearchTerm, pagination.pageIndex, pagination.pageSize, refetch]);

  useEffect(() => {
    if (yachtsResponse) {
      dispatch(setYachtsResponse(yachtsResponse));
    }
  }, [yachtsResponse, dispatch]);

  const yachtsData = useMemo(() => {
    return yachtsResponse?.data || [];
  }, [yachtsResponse]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    resetSearch();
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const columns = useMemo(
    () => [
      {
        header: 'Logo',
        accessorKey: 'logo',
        cell: ({ row }: DataGridRowProps) => {
          const logo = row.original.logo;
          if (!logo) {
            return (
              <div className="h-10 w-10 bg-gray-100 rounded flex items-center justify-center">
                <KeenIcon
                  type="filled"
                  icon="image"
                  className="text-gray-400"
                />
              </div>
            );
          }
          const logoUrl =
            typeof logo === 'string' ? logo : URL.createObjectURL(logo);
          return (
            <img
              src={logoUrl}
              alt="Yacht logo"
              className="h-10 w-10 object-cover rounded"
            />
          );
        },
      },
      {
        header: 'Name',
        accessorKey: 'name',
        cell: ({ row }: DataGridRowProps) => {
          const handleClubClick = async () => {
            const clubId = row.original.id;
            if (!hasPermission('club_management_read') || !clubId) {
              return;
            }

            try {
              // Fetch the club's current plan
              const currentPlan =
                await SubscriptionsService.getCurrentClubPlan(clubId);
              const planId = currentPlan?.subscription?.id;

              if (planId) {
                // Fetch capabilities for the plan
                const capabilities =
                  await SubscriptionsService.getSubscriptionCapabilities(
                    planId,
                    clubId
                  );

                // Extract unique keys and store in Redux
                const uniqueKeys =
                  capabilities
                    ?.filter((cap) => cap.include === true)
                    .map((cap) => cap.unique_key) || [];

                dispatch(setClubCapabilities({ capabilities: uniqueKeys }));
              } else {
                // No plan found, clear capabilities
                dispatch(setClubCapabilities({ capabilities: [] }));
              }

              // Navigate to club details
              navigate(`/club-management/${clubId}`);
            } catch (error) {
              console.error('Failed to fetch club capabilities:', error);
              // Navigate anyway, capabilities will be empty
              dispatch(setClubCapabilities({ capabilities: [] }));
              navigate(`/club-management/${clubId}`);
            }
          };

          return (
            <div className="flex items-center gap-3">
              {hasPermission('club_management_read') ? (
                <button
                  onClick={handleClubClick}
                  className="font-medium text-blue-700 hover:underline hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 contrast:text-blue-400 contrast:hover:text-blue-300 text-left"
                >
                  {row.original.name}
                </button>
              ) : (
                <span className="font-medium">{row.original.name}</span>
              )}
            </div>
          );
        },
      },
      {
        header: 'Description',
        accessorKey: 'description',
        cell: ({ row }: DataGridRowProps) => (
          <div className="line-clamp-2">{row.original.description || '-'}</div>
        ),
      },
      {
        header: 'Location',
        accessorKey: 'location',
        cell: ({ row }: DataGridRowProps) => (
          <div>{row.original.location || '-'}</div>
        ),
      },
      {
        header: 'Club Admin',
        accessorKey: 'yacht_club_admin',
        cell: ({ row }: DataGridRowProps) => (
          <div>
            {row.original.yachtClubAdmin?.name ||
              row.original.yachtClubAdmin?.email ||
              '-'}
          </div>
        ),
      },
      {
        header: 'Website',
        accessorKey: 'website',
      },
      {
        header: 'Contact Email',
        accessorKey: 'contact_email',
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: DataGridRowProps) => (
          <div className="flex items-center gap-2">
            {hasPermission('club_management_read') && (
              <button
                onClick={async () => {
                  const clubId = row.original.id;
                  if (!clubId) return;

                  try {
                    // Fetch the club's current plan
                    const currentPlan =
                      await SubscriptionsService.getCurrentClubPlan(clubId);
                    const planId = currentPlan?.subscription?.id;

                    if (planId) {
                      // Fetch capabilities for the plan
                      const capabilities =
                        await SubscriptionsService.getSubscriptionCapabilities(
                          planId,
                          clubId
                        );

                      // Extract unique keys and store in Redux
                      const uniqueKeys =
                        capabilities
                          ?.filter((cap) => cap.include === true)
                          .map((cap) => cap.unique_key) || [];

                      dispatch(
                        setClubCapabilities({ capabilities: uniqueKeys })
                      );
                    } else {
                      // No plan found, clear capabilities
                      dispatch(setClubCapabilities({ capabilities: [] }));
                    }

                    // Navigate to club details
                    navigate(`/club-management/${clubId}`);
                  } catch (error) {
                    console.error('Failed to fetch club capabilities:', error);
                    // Navigate anyway, capabilities will be empty
                    dispatch(setClubCapabilities({ capabilities: [] }));
                    navigate(`/club-management/${clubId}`);
                  }
                }}
                className="btn btn-sm btn-icon btn-light btn-outline"
                aria-label={`View ${row.original.name}`}
              >
                <KeenIcon type="filled" icon="eye" />
              </button>
            )}
            {hasPermission('club_management_update') && (
              <button
                onClick={() =>
                  navigate(`/club-management/edit/${row.original.id}`)
                }
                className="btn btn-sm btn-icon btn-light btn-outline"
                aria-label={`Edit ${row.original.name}`}
              >
                <KeenIcon type="filled" icon="pencil" />
              </button>
            )}
            {hasPermission('club_management_delete') && (
              <button
                type="button"
                onClick={() =>
                  setDeleteState({
                    isOpen: true,
                    yachtId: row.original.id as string,
                  })
                }
                className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Delete"
                aria-label={`Delete ${row.original.name}`}
                data-testid="delete-button"
              >
                <KeenIcon type="filled" icon="trash" className="text-red-500" />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [hasPermission, dispatch, navigate]
  );

  const handleDelete = async (id: string) => {
    try {
      await deleteYacht(id).unwrap();
      enqueueSnackbar('Yacht deleted successfully', { variant: 'success' });
      setDeleteState({ isOpen: false, yachtId: null });

      // Invalidate cache
      dispatch(
        yachtManagementApi.util.invalidateTags([
          { type: 'Yachts', id: 'LIST' },
          { type: 'Yachts', id },
        ])
      );

      // The useEffect hook will automatically trigger refetch
    } catch {
      enqueueSnackbar('Failed to delete yacht', { variant: 'error' });
    }
  };

  if (errors?.fetch) {
    return (
      <div className="p-4 text-red-500" data-testid="error-message">
        {errors.fetch}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="card card-grid min-w-full">
        <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-yacht"
              aria-label="Search clubs"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                id="search-yacht"
                placeholder="Search clubs"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search clubs by name"
              />
            </label>
          </div>
          {/* Clear Filters Button */}
          {searchTerm !== '' && (
            <div className="flex items-center">
              <button
                className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
                onClick={resetFilters}
                aria-label="Clear all filters"
                title="Clear all filters"
              >
                <KeenIcon
                  type="solid"
                  icon="arrows-circle"
                  className="text-gray-600"
                />
                Clear Filters
              </button>
            </div>
          )}
        </div>

        <div className="p-4">
          {isLoading || isFetching ? (
            <div className="text-center py-4">Loading...</div>
          ) : isError ? (
            <div className="text-center py-4 text-red-500">
              Failed to load yachts. Please try again.
            </div>
          ) : (
            <DataGrid
              columns={columns}
              data={yachtsData}
              rowSelect={false}
              loading={isLoading || isFetching}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                more: true,
                totalItems: yachtsResponse?.totalItems || 0,
                totalPages: yachtsResponse?.totalPages,
                setPagination,
                info: `{from} - {to} of {count}`,
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              sorting={[{ id: 'users', desc: false }]}
              serverSide={true}
              onFilterReset={resetFilters}
              hasActiveFilters={searchTerm !== ''}
            />
          )}
        </div>
      </div>
      <ConfirmationDialog
        open={deleteState.isOpen}
        onClose={() => setDeleteState({ isOpen: false, yachtId: null })}
        onConfirm={() => handleDelete(deleteState.yachtId as string)}
        message="Are you sure you want to delete this yacht?"
        confirmButtonText="Delete"
      />
    </div>
  );
};
